import React, { useEffect } from 'react';
import { MantineProvider, Box } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { mantineTheme } from './presentation/styles/theme/mantineTheme';
import { AppRoutes } from './presentation/routing/AppRoutes';
import { useAuthStore } from './application/store/authStore';
import { NOTIFICATION_CONFIG } from './infrastructure/notifications';
import { ErrorBoundary, ErrorFallback } from './presentation/components/ErrorBoundary';
import { OfflineBanner } from './presentation/components/OfflineBanner';
import { LoadingOverlay } from './presentation/components/common/LoadingOverlay';
import { queryClient } from './application/queries/queryClient';

// Import Mantine core styles
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
// sprint-110 S1 — TipTap rich-text editor (used by the user-authored email
// template editor under /MineMiner/email-templates/:emailTemplateId).
// sprint-161 (epic-026 Sprint D, D1): the `@mantine/tiptap/styles.css` import
// moved out of this global entry CSS into `RichTextEmailEditor.tsx` (its sole
// consumer, reachable only via the lazy email-template route) so it no longer
// inflates the render-blocking entry stylesheet that gates first paint.

/**
 * Main App component — MineMiner SPA.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Forked from `Frontend/`'s
 * App and slimmed: StripeProvider and GoogleReCaptchaProvider are dropped
 * (no payment surface, no reCAPTCHA surface in the MineMiner SPA).
 */
export const App: React.FC = (): React.ReactElement => {
  const loadCurrentUser = useAuthStore((state) => state.loadCurrentUser);
  const isAuthLoading = useAuthStore((state) => state.isLoading);
  const loadingMessage = useAuthStore((state) => state.loadingMessage);

  // Initialize authentication on app startup.
  useEffect(() => {
    void loadCurrentUser();
  }, [loadCurrentUser]);

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <MantineProvider theme={mantineTheme}>
          <ModalsProvider>
            <Notifications
              position={NOTIFICATION_CONFIG.POSITION}
              zIndex={NOTIFICATION_CONFIG.Z_INDEX}
              limit={NOTIFICATION_CONFIG.MAX_NOTIFICATIONS}
            />
            <OfflineBanner />
            <BrowserRouter>
              <ErrorBoundary fallback={ErrorFallback}>
                <Box pos="relative" mih="100vh">
                  <LoadingOverlay
                    visible={isAuthLoading}
                    message={loadingMessage ?? 'Loading...'}
                  />
                  <AppRoutes />
                </Box>
              </ErrorBoundary>
            </BrowserRouter>
          </ModalsProvider>
        </MantineProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
